/* global window, document, fetch, MutationObserver */
/**
 * Livadi / Metrix Ring Builder — section JavaScript
 *
 * WHY NO LIQUID TAGS IN THIS FILE:
 *   Shopify does NOT process Liquid in .js asset files. Any {{ section.id }}
 *   written here would be sent to the browser literally. Section IDs are
 *   instead passed via window.__metrixQueue, which is populated by a small
 *   inline <script> block in livadi-builder.liquid where Liquid IS processed.
 *
 * Boot sequence:
 *   1. Clear stale Metrix localStorage keys so Angular never reads a cached
 *      container on boot (configuratorContainer*, metrix*, recreate-*).
 *   2. The Liquid's inline <script> has already pushed the section ID into
 *      window.__metrixQueue before this file executes.
 *   3. On DOMContentLoaded (or immediately if DOM is ready) drain the queue —
 *      calling initSection(sectionId) for each entry.
 *   4. Replace the queue array with a live proxy so theme-editor re-renders
 *      (which push again) also call initSection directly.
 *
 * Loading overlay strategy:
 *   - The overlay is FULLY OPAQUE until the bridge's `containerUpdated` event
 *     fires AFTER we have called bridge.initContainer with the correct container.
 *   - `bridgeContainerInited` flag prevents hiding the overlay for any early
 *     `containerUpdated` Angular fires during its own boot — the root cause of
 *     the visible ring-flash.
 *   - A 15-second safety-net timeout hides the loader if the event never fires.
 *
 * Guards:
 *   - window.__metrixInitializedSections  — prevents double-init of a section.
 *   - window.__metrixIntegrationLoaded    — prevents injecting integration.js
 *     more than once (it boots Angular once; the event fires once).
 *
 * Request flow:
 *   This section intentionally does NOT add configured rings to the Shopify cart.
 *   Shopify would price that cart line from the base product variant, not from
 *   the Metrix configuration. The product page must remain integrated; forcing
 *   standalone mode makes the Metrix Angular app try to route Shopify
 *   `/products/...` URLs. We pass `cartMode=requestList` in integrated mode and
 *   normalise Metrix's labels to Summary / Request. When Metrix emits the
 *   request action, we open Metrix's own `contact-jeweler` order request modal.
 */

(function (global) {
  "use strict";

  /* ── Nuke ALL Metrix localStorage immediately ─────────────────────────── */
  try {
    var _keysToRemove = [];
    for (var _i = localStorage.length - 1; _i >= 0; _i--) {
      var _k = localStorage.key(_i);
      if (_k && /configuratorContainer|metrix|^recreate-/i.test(_k)) {
        _keysToRemove.push(_k);
      }
    }
    _keysToRemove.forEach(function (k) {
      localStorage.removeItem(k);
    });
    if (_keysToRemove.length) {
      console.log("[Metrix] Cleared stale localStorage keys:", _keysToRemove);
    }
  } catch (_) {
    /* private browsing / blocked storage — ignore */
  }

  /* ── Per-section and global script guards ─────────────────────────────── */
  var _initializedSections =
    global.__metrixInitializedSections ||
    (global.__metrixInitializedSections = {});

  /* ── Single-section boot ──────────────────────────────────────────────── */

  function initSection(sectionId) {
    if (_initializedSections[sectionId]) {
      console.log(
        "[Metrix] Section already initialised — skipping:",
        sectionId,
      );
      return;
    }
    _initializedSections[sectionId] = true;

    var wrapper = document.querySelector(
      '[data-metrix-section="' + sectionId + '"]',
    );
    if (!wrapper) {
      console.error("[Metrix] Wrapper not found for section:", sectionId);
      return;
    }

    var jewelerId = wrapper.dataset.jewelerId;
    var bmaBaseUrl = wrapper.dataset.baseUrl;
    var snapshotId = wrapper.dataset.snapshotId;
    var language = wrapper.dataset.language || "en";
    var cartMode = "requestList";
    var loader = wrapper.querySelector(".metrix-loading");

    console.log(
      "[Metrix] Init — snapshotId:",
      snapshotId,
      "| jeweler:",
      jewelerId,
    );

    /* ── Pre-fetch container immediately ────────────────────────────────────
     * Angular's boot sequence (loading sources.js + Angular chunks) takes
     * several seconds. Our container fetch takes ~200 ms. By firing it NOW —
     * in parallel with integration.js loading — the containerId is ready the
     * instant configuratorInitialized fires, so bridge.initContainer can be
     * called synchronously with no async gap during which Angular could
     * independently create a default container and overwrite ours.
     */
    var containerPromise = fetch(
      bmaBaseUrl + "/appconfigurator/proxy/containers",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ snapshotId: snapshotId }),
      },
    )
      .then(function (r) {
        if (!r.ok) throw new Error("createContainer HTTP " + r.status);
        return r.json();
      })
      .then(function (d) {
        console.log("[Metrix] containerId pre-fetched:", d.containerId);
        return d.containerId;
      });

    /* ── Loading helpers ─────────────────────────────────────────────────── */
    var containerReady = false;
    var bridgeContainerInited = false;
    var loaderTimer = null;

    function hideLoader() {
      if (containerReady) return;
      containerReady = true;
      if (loader) loader.setAttribute("hidden", "");
      if (loaderTimer) {
        clearTimeout(loaderTimer);
        loaderTimer = null;
      }
      console.log("[Metrix] Loader hidden — ring is visible.");
    }

    function armTimeout() {
      loaderTimer = setTimeout(function () {
        if (!containerReady) {
          console.warn("[Metrix] Loader timeout (15 s) — forcing hide.");
          hideLoader();
        }
      }, 15000);
    }

    function normaliseRequestButtons() {
      var root =
        wrapper.querySelector("#metrix-configurator") ||
        document.getElementById("metrix-configurator");

      if (!root) return;

      var controls = root.querySelectorAll("button, a, [role='button']");
      Array.prototype.forEach.call(controls, function (control) {
        var text = (control.textContent || "").replace(/\s+/g, " ").trim();
        var label = control.getAttribute("aria-label") || "";
        var combined = (text + " " + label).trim();

        if (/wishlist/i.test(combined)) {
          control.setAttribute("hidden", "");
          control.setAttribute("aria-hidden", "true");
          control.style.display = "none";
          return;
        }

        if (/add to order requests?|order requests?/i.test(combined)) {
          control.textContent = "Request";
          control.setAttribute("aria-label", "Request");
          control.setAttribute("title", "Request");
        }
      });
    }

    function observeRequestButtons() {
      var root =
        wrapper.querySelector("#metrix-configurator") ||
        document.getElementById("metrix-configurator");

      if (!root) return;

      normaliseRequestButtons();

      if (global.MutationObserver) {
        var observer = new MutationObserver(normaliseRequestButtons);
        observer.observe(root, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }
    }

    function isMetrixRequestAction(action) {
      return /^(requestListCart|requestCart|request)$/i.test(action || "");
    }

    function openNativeRequestModal(bridge) {
      if (!bridge || !bridge.modals || !bridge.auth) {
        console.warn("[Metrix] Native request modal unavailable.");
        return false;
      }

      function open(jeweler) {
        if (!jeweler || !jeweler._id) {
          console.warn("[Metrix] Jeweler context missing; cannot open request modal.");
          return;
        }

        var showModal = function () {
          bridge.modals.openModal({
            id: "contact-jeweler",
            title: "modal.contact-jeweler.title",
            context: jeweler,
          });
        };

        if (bridge.zone && typeof bridge.zone.run === "function") {
          bridge.zone.run(showModal);
        } else {
          showModal();
        }
      }

      var customJeweler = bridge.auth.customJeweler;
      if (customJeweler && customJeweler.value) {
        open(customJeweler.value);
        return true;
      }

      if (customJeweler && typeof customJeweler.then === "function") {
        customJeweler.then(open, function (err) {
          console.warn("[Metrix] Failed to resolve jeweler context:", err);
        });
        return true;
      }

      console.warn("[Metrix] Custom jeweler context missing.");
      return false;
    }

    /* ── configuratorInitialized listener ────────────────────────────────
     * Registered BEFORE integration.js is injected so we never miss the event.
     * integration.js calls configureMetrixRoot() directly — it does NOT call
     * window.onBootstrapMetrixModule (that hook is dead code, removed).
     * Named function so we can removeEventListener on first invocation.
     */
    function onConfiguratorInitialized(event) {
      document.removeEventListener(
        "metrix:configuratorInitialized",
        onConfiguratorInitialized,
      );

      console.log("[Metrix] configuratorInitialized");
      var bridge = event.detail && event.detail.bridge;
      if (!bridge) {
        console.error("[Metrix] Bridge missing in event detail.");
        hideLoader();
        return;
      }

      try {
        bridge.addLanguages([language]);
      } catch (e) {
        console.warn("[Metrix] addLanguages unavailable:", e);
      }
      try {
        bridge.currentLanguage = language;
      } catch (e) {}

      try {
        bridge.setContextValue("cartMode", cartMode);
      } catch (e) {}

      observeRequestButtons();

      /*
       * bridge.integration.mode("configurator") was removed — bridge.integration
       * does not exist on the Metrix bridge object, causing the entire handler
       * to throw before any listeners or bridge.initContainer were set up.
       * Always pass null so Metrix uses its default integrated mode.
       */
      var mode = null;
      console.log("[Metrix] mode: null | target snapshot:", snapshotId);

      bridge.listen("containerUpdated", function () {
        normaliseRequestButtons();
        if (!containerReady && bridgeContainerInited) {
          /*
           * Wait for the browser to actually paint the ring before removing the
           * overlay. Without this, the overlay can disappear one frame before
           * the ring pixels hit the screen, causing a brief white flash.
           */
          requestAnimationFrame(function () {
            console.log("[Metrix] containerUpdated + rAF — ring painted.");
            hideLoader();
          });
        }
      });

      bridge.listen("containerChanged", function (detail) {
        if (detail && detail.containerId) {
          wrapper._metrixContainerId = detail.containerId;
          console.log("[Metrix] containerChanged →", detail.containerId);
        }
      });

      bridge.listen("stepChanged", function () {});

      bridge.listen("buttonClicked", function (detail) {
        /*
         * bridge.listen callbacks receive the raw CustomEvent object, NOT just
         * its payload. The actual data lives in detail.detail (the CustomEvent's
         * inner detail property). Unwrap it first, then normalise to an action
         * string — Metrix delivers it as either a plain string or an object with
         * varying property names (action / button / name) across versions.
         */
        var payload =
          detail && detail.detail !== undefined ? detail.detail : detail;

        var action;
        if (typeof payload === "string") {
          action = payload;
        } else if (payload && typeof payload === "object") {
          action =
            payload.action || payload.button || payload.name || payload.id;
        }

        console.log(
          "[Metrix] buttonClicked — payload:",
          JSON.stringify(payload),
          "| resolved action:",
          action,
        );

        if (isMetrixRequestAction(action)) {
          openNativeRequestModal(bridge);
        }
      });

      armTimeout();

      /*
       * Use the pre-fetched container promise (started when initSection ran,
       * before integration.js was injected). The container is almost certainly
       * already resolved by now, so this .then executes synchronously or on
       * the next microtask — giving Angular no opportunity to create its own
       * default container between configuratorInitialized and initContainer.
       */
      containerPromise
        .then(function (cid) {
          wrapper._metrixContainerId = cid;
          bridgeContainerInited = true; // set BEFORE initContainer
          bridge.initContainer(mode, cid);

          /*
           * Sweep any stale recreate-{id} entries that Angular wrote during
           * its boot cycle (for containers other than ours). If left in place,
           * Angular processes these after initContainer and briefly switches to
           * the default Livadi ring — the flash that was still visible.
           */
          try {
            var keysToNuke = [];
            for (var ki = localStorage.length - 1; ki >= 0; ki--) {
              var kk = localStorage.key(ki);
              if (kk && /^recreate-/i.test(kk) && kk !== "recreate-" + cid) {
                keysToNuke.push(kk);
              }
            }
            keysToNuke.forEach(function (k) {
              localStorage.removeItem(k);
            });
            if (keysToNuke.length) {
              console.log(
                "[Metrix] Post-init stale recreate sweep:",
                keysToNuke,
              );
            }
          } catch (_) {}
        })
        .catch(function (err) {
          console.error(
            "[Metrix] createContainer failed:",
            err,
            "— null fallback",
          );
          bridgeContainerInited = true;
          bridge.initContainer(mode, null);
        });
    }

    document.addEventListener(
      "metrix:configuratorInitialized",
      onConfiguratorInitialized,
    );

    /* ── Inject integration.js (only once globally) ──────────────────────── */
    if (global.__metrixIntegrationLoaded) {
      console.log("[Metrix] integration.js already loaded — skipping inject.");
      return;
    }
    global.__metrixIntegrationLoaded = true;

    var script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src =
      bmaBaseUrl +
      "/integration.js?jewelerId=" +
      encodeURIComponent(jewelerId) +
      "&cartMode=" +
      encodeURIComponent(cartMode);
    script.addEventListener("load", function () {
      console.log("[Metrix] integration.js loaded.");
    });
    script.addEventListener("error", function () {
      console.error("[Metrix] Failed to load integration.js from:", script.src);
      hideLoader();
    });
    console.log("[Metrix] Loading:", script.src);
    document.head.appendChild(script);
  }

  /* ── Queue processor ──────────────────────────────────────────────────── */
  /*
   * livadi-builder.liquid contains an inline <script> (where Liquid IS processed):
   *
   *   window.__metrixQueue = window.__metrixQueue || [];
   *   window.__metrixQueue.push('{{ section.id }}');
   *
   * We drain that queue here, then replace it with a live proxy so future
   * theme-editor re-renders (which push again) call initSection directly.
   */
  function boot() {
    var q = global.__metrixQueue;
    if (Array.isArray(q)) {
      q.forEach(initSection);
    }
    global.__metrixQueue = { push: initSection };
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})(window);
